import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Missile {
  public mesh: THREE.Group;
  public body: CANNON.Body;
  public isActive: boolean = true;
  public owner: 'player' | 'enemy';
  public damage: number;
  public areaOfEffect: number;
  public hasExploded: boolean = false;

  private lifetime: number = 8;
  private age: number = 0;
  private target: THREE.Vector3 | null = null;
  private trackingSpeed: number = 3; // How fast the missile turns toward target

  constructor(
    position: THREE.Vector3,
    direction: THREE.Vector3,
    owner: 'player' | 'enemy',
    damage: number,
    areaOfEffect: number,
    target?: THREE.Vector3
  ) {
    this.owner = owner;
    this.damage = damage;
    this.areaOfEffect = areaOfEffect;
    this.target = target || null;

    // 创建导弹网格组
    this.mesh = new THREE.Group();

    // 导弹主体
    const bodyGeometry = new THREE.CylinderGeometry(0.15, 0.15, 1, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      metalness: 0.7,
      roughness: 0.3
    });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    bodyMesh.rotation.x = Math.PI / 2;
    bodyMesh.castShadow = true;
    this.mesh.add(bodyMesh);

    // 导弹头部（锥形）
    const noseGeometry = new THREE.ConeGeometry(0.15, 0.3, 8);
    const noseMaterial = new THREE.MeshStandardMaterial({
      color: owner === 'player' ? 0x00ff00 : 0xff0000,
      emissive: owner === 'player' ? 0x00ff00 : 0xff0000,
      emissiveIntensity: 0.5
    });
    const noseMesh = new THREE.Mesh(noseGeometry, noseMaterial);
    noseMesh.position.z = 0.65;
    noseMesh.rotation.x = -Math.PI / 2;
    noseMesh.castShadow = true;
    this.mesh.add(noseMesh);

    // 尾焰
    const flameGeometry = new THREE.ConeGeometry(0.1, 0.4, 6);
    const flameMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.8
    });
    const flameMesh = new THREE.Mesh(flameGeometry, flameMaterial);
    flameMesh.position.z = -0.7;
    flameMesh.rotation.x = Math.PI / 2;
    this.mesh.add(flameMesh);

    this.mesh.position.copy(position);

    // 设置初始朝向
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      direction.clone().normalize()
    );
    this.mesh.quaternion.copy(quaternion);

    // 创建物理体
    const shape = new CANNON.Cylinder(0.15, 0.15, 1, 8);
    this.body = new CANNON.Body({
      mass: 2,
      shape: shape,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      linearDamping: 0.05,
      angularDamping: 0.9
    });

    // 设置初始速度
    const velocity = direction.clone().multiplyScalar(20);
    this.body.velocity.set(velocity.x, velocity.y, velocity.z);

    // 设置初始旋转
    this.body.quaternion.copy(quaternion as any);

    // 添加用户数据
    this.body.userData = {
      type: 'missile',
      missile: this,
      owner: owner
    };
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    this.age += deltaTime;

    // 超时销毁
    if (this.age >= this.lifetime) {
      this.explode();
      return;
    }

    // 追踪目标
    if (this.target && this.age > 0.5) { // 延迟0.5秒后开始追踪
      const currentPos = new THREE.Vector3(
        this.body.position.x,
        this.body.position.y,
        this.body.position.z
      );

      const directionToTarget = new THREE.Vector3()
        .subVectors(this.target, currentPos)
        .normalize();

      // 获取当前速度方向
      const currentVelocity = new THREE.Vector3(
        this.body.velocity.x,
        this.body.velocity.y,
        this.body.velocity.z
      );
      const speed = currentVelocity.length();

      // 平滑转向目标
      const newDirection = currentVelocity
        .normalize()
        .lerp(directionToTarget, this.trackingSpeed * deltaTime)
        .normalize();

      // 更新速度
      const newVelocity = newDirection.multiplyScalar(speed);
      this.body.velocity.set(newVelocity.x, newVelocity.y, newVelocity.z);

      // 更新朝向
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), newDirection);
      this.mesh.quaternion.copy(quaternion);
      this.body.quaternion.copy(quaternion as any);
    }

    // 同步位置
    this.mesh.position.copy(this.body.position as any);
  }

  explode(): void {
    if (this.hasExploded) return;
    this.hasExploded = true;
    this.destroy();
  }

  destroy(): void {
    this.isActive = false;
  }

  getExplosionPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }
}
